import { createFileRoute, Link, Outlet, redirect, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { LayoutDashboard, Package, MapPin, User, LogOut, Menu, X, ReceiptText, Star, Handshake } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import logoAsset from "@/assets/fixhen-logo.png.asset.json";

export const Route = createFileRoute("/_account")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      throw redirect({ to: "/auth", search: { redirect: location.href } });
    }
    if (!data.user.email_confirmed_at) {
      throw redirect({
        to: "/verify-email",
        search: { email: data.user.email ?? "", redirect: location.href } as never,
      });
    }
    return { user: data.user };
  },

  head: () => ({
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  component: AccountShell,
});

const nav = [
  { to: "/account", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/account/orders", label: "Orders", icon: Package },
  { to: "/account/reviews", label: "Reviews", icon: Star },
  { to: "/account/referrals", label: "Referrals", icon: Handshake },
  { to: "/account/addresses", label: "Addresses", icon: MapPin },
  { to: "/account/billing", label: "Billing details", icon: ReceiptText },
  { to: "/account/profile", label: "Profile", icon: User },
];


function AccountShell() {
  const { user } = Route.useRouteContext() as { user: { email?: string; user_metadata?: any } };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => setMobileOpen(false), [path]);

  const initials =
    ((user.user_metadata?.first_name?.[0] ?? "") + (user.user_metadata?.last_name?.[0] ?? "")).toUpperCase() ||
    (user.email?.[0] ?? "U").toUpperCase();
  const displayName =
    [user.user_metadata?.first_name, user.user_metadata?.last_name].filter(Boolean).join(" ") || user.email;

  async function signOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/", replace: true });
  }

  function isActive(to: string, exact?: boolean) {
    return exact ? path === to : path === to || path.startsWith(to + "/");
  }

  const sidebar = (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-3 border-b border-slate-100 pb-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#7DD3FC] to-[#2563EB] text-sm font-bold text-white">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
          <p className="truncate text-xs text-slate-500">{user.email}</p>
        </div>
      </div>
      <nav className="space-y-1">
        {nav.map((n) => {
          const Icon = n.icon;
          const active = isActive(n.to, n.exact);
          return (
            <Link
              key={n.to}
              to={n.to as never}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                active ? "bg-[#2563EB] text-white" : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{n.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={signOut}
          className="mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          Sign out
        </button>
      </nav>
    </div>
  );

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-slate-50">
      {/* Top bar */}
      <header className="shrink-0 border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
          <button
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-slate-200 lg:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <img src={logoAsset.url} alt="FIXHEN" width={28} height={28} className="h-7 w-7 shrink-0" />
            <span className="truncate text-base font-black tracking-tight text-ink">FIXHEN</span>
          </Link>
          <span className="ml-2 hidden text-sm font-medium text-slate-400 sm:inline">My account</span>
          <div className="ml-auto flex items-center gap-3">
            <Link to="/shop" className="hidden text-sm font-medium text-slate-600 hover:text-slate-900 sm:inline">
              Continue shopping →
            </Link>
            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#172554] text-sm font-bold text-white">
              {initials}
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto grid min-h-0 w-full max-w-7xl flex-1 grid-cols-1 gap-6 px-4 py-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden min-h-0 overflow-y-auto lg:block">{sidebar}</aside>

        {/* Mobile sidebar overlay */}
        {mobileOpen && (
          <>
            <button
              aria-label="Close menu"
              className="fixed inset-0 top-14 z-30 bg-black/30 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <aside className="fixed inset-x-0 top-14 z-40 max-h-[calc(100dvh-3.5rem)] overflow-y-auto p-4 lg:hidden">
              {sidebar}
            </aside>
          </>
        )}

        <main className="min-w-0 overflow-y-auto overflow-x-hidden pb-4 pr-1 -mr-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}